import { Model, type Web3DViewer } from '@technology/web3d';
import type { Api } from '@technology/web3d/dist/esnext/Api.js';
import type { Caster } from '@technology/web3d/dist/esnext/Picker/Caster.js';
import type { IIntersection } from '@technology/web3d/dist/esnext/Picker/IIntersection.js';
import {
  Group,
  Vector3,
  type ThreejsModel,
} from '@technology/web3d-plugin-threejs';
import { TextMesh } from './text-mesh';
import {
  areTextGuidSelectionsEqual,
  collectTextGuidsFromObjectIds,
} from './text-pick';
import { textsEqual } from './text-object-equality';
import type { ITextObject, TextObjectPatch } from './types/text-object';
import { TEXT_UPSERT_BATCH_SIZE, yieldToMainThread } from './upsert-chunking';
import {
  InsertionPointHandle,
  type GetDragConstraintSettings,
} from './insertion-point-handle';
import type { TextDragConstraintSettings } from './drag-constraints';

export const TEXT_SELECTION_CHANGED_EVENT = 'textselectionchanged';
export const TEXT_INSERTION_POINT_CHANGED_EVENT = 'textinsertionpointchanged';
export const TEXT_CREATED_EVENT = 'textcreated';
export const TEXT_DELETED_EVENT = 'textdeleted';

/**
 * Wins over depth-picked surfaces/terrain (priority 0) in
 * `Picker.reduceIntersections`, so labels are selectable through occluding
 * geometry. Stays below line-editing lines/points (3+) so precise line picks
 * keep priority over the label quad.
 */
export const TEXT_PICK_PRIORITY = 1;

export class TextEntity implements ITextObject {
  public readonly guid: string;
  public content: string;
  public insertionPoint: ITextObject['insertionPoint'];
  public textHeight: number;
  public rotation: number;
  public horizontalAttachment: ITextObject['horizontalAttachment'];
  public verticalAttachment: ITextObject['verticalAttachment'];
  public typefaceName?: string;
  public widthFactor: number;
  public obliqueAngle: number;
  public boxWidth: number;
  public lineSpacingFactor: number;
  public lineSpacingStyle: ITextObject['lineSpacingStyle'];
  public drawDirection: ITextObject['drawDirection'];
  public color: string;
  public metaData: ITextObject['metaData'];
  public editable: boolean;
  public modelSpaceId?: string;

  constructor(source: ITextObject) {
    this.guid = source.guid;
    this.content = source.content;
    this.insertionPoint = { ...source.insertionPoint };
    this.textHeight = source.textHeight;
    this.rotation = source.rotation;
    this.horizontalAttachment = source.horizontalAttachment;
    this.verticalAttachment = source.verticalAttachment;
    this.typefaceName = source.typefaceName;
    this.widthFactor = source.widthFactor;
    this.obliqueAngle = source.obliqueAngle;
    this.boxWidth = source.boxWidth;
    this.lineSpacingFactor = source.lineSpacingFactor;
    this.lineSpacingStyle = source.lineSpacingStyle;
    this.drawDirection = source.drawDirection;
    this.color = source.color;
    this.metaData = { ...source.metaData };
    this.editable = source.editable;
    this.modelSpaceId = source.modelSpaceId;
  }

  public toObject(): ITextObject {
    return {
      guid: this.guid,
      content: this.content,
      insertionPoint: { ...this.insertionPoint },
      textHeight: this.textHeight,
      rotation: this.rotation,
      horizontalAttachment: this.horizontalAttachment,
      verticalAttachment: this.verticalAttachment,
      typefaceName: this.typefaceName,
      widthFactor: this.widthFactor,
      obliqueAngle: this.obliqueAngle,
      boxWidth: this.boxWidth,
      lineSpacingFactor: this.lineSpacingFactor,
      lineSpacingStyle: this.lineSpacingStyle,
      drawDirection: this.drawDirection,
      color: this.color,
      metaData: { ...this.metaData },
      editable: this.editable,
      modelSpaceId: this.modelSpaceId,
    };
  }
}

export interface TextEditingModelOptions {
  readonly modelId: string;
  readonly viewer: Web3DViewer;
  readonly threejsModel: ThreejsModel;
  readonly api: Api;
}

/**
 * Pick-only Web3D model for CAD text labels.
 *
 * The label meshes live inside the shared `threejs` model so area selection
 * and selection highlighting work unchanged. This model only contributes a
 * `pick()` implementation with an elevated pick priority, which is how the
 * standard `SelectionTool` selects labels even when a surface or mesh is in
 * front of them. The winning intersection reports the `threejs` model, so
 * single-click selection flows through the exact same `viewer.selection`
 * path as area selection.
 */
export class TextEditingModel extends Model {
  private readonly viewer: Web3DViewer;
  private readonly api: Api;
  private readonly threejsModel: ThreejsModel;
  private readonly textGroup: Group;
  private readonly handleGroup: Group;
  private readonly entities = new Map<string, TextEntity>();
  private readonly meshes = new Map<string, TextMesh>();
  private readonly insertionPointHandles = new Map<
    string,
    InsertionPointHandle
  >();
  private selectedGuids: string[] = [];
  private disposed = false;
  private boundsDirty = false;
  private getDragConstraintSettings: GetDragConstraintSettings =
    (): TextDragConstraintSettings => ({});

  private readonly handleViewerSelection = (): void => {
    const guids = this.getTextGuidsFromViewerThreeJsSelection();
    if (areTextGuidSelectionsEqual(guids, this.selectedGuids)) {
      return;
    }

    this.setSelectedEntities(guids);
    this.emitSelectionChanged();
  };

  constructor(options: TextEditingModelOptions) {
    super(options.modelId);
    this.viewer = options.viewer;
    this.api = options.api;
    this.threejsModel = options.threejsModel;
    this.textGroup = new Group();
    this.textGroup.name = `${options.modelId}-text-root`;
    this.handleGroup = new Group();
    this.handleGroup.name = `${options.modelId}-text-handles`;
    this.threejsModel.add(this.textGroup);
    this.threejsModel.add(this.handleGroup);
    this.threejsModel.refreshBoundingBox();

    this.viewer.selection.subscribe(this.handleViewerSelection);
  }

  public setDragConstraintSettingsProvider(
    provider: GetDragConstraintSettings
  ): void {
    this.getDragConstraintSettings = provider;
  }

  public pick(caster: Caster): Promise<IIntersection> | undefined {
    if (this.meshes.size === 0) {
      return undefined;
    }

    const intersections = caster.intersectObject(this.textGroup, true);
    if (intersections.length === 0) {
      return undefined;
    }

    const hit = intersections[0];
    return Promise.resolve({
      ...hit,
      id: hit.object.id,
      model: this.threejsModel,
      caster,
      distanceToRay: 0,
      pickPriority: TEXT_PICK_PRIORITY,
    } as IIntersection);
  }

  public override dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;

    this.viewer.selection.unsubscribe(this.handleViewerSelection);

    for (const handle of this.insertionPointHandles.values()) {
      this.handleGroup.remove(handle.object3D);
      handle.dispose();
    }
    this.insertionPointHandles.clear();

    for (const mesh of this.meshes.values()) {
      this.textGroup.remove(mesh);
      mesh.dispose();
    }
    this.meshes.clear();
    this.entities.clear();
    this.threejsModel.remove(this.textGroup);
    this.threejsModel.remove(this.handleGroup);
    this.threejsModel.refreshBoundingBox();
    super.dispose();
  }

  public allEntities(): TextEntity[] {
    return Array.from(this.entities.values());
  }

  public getOwnedGroupIds(): number[] {
    return [this.textGroup.id, this.handleGroup.id];
  }

  public getTextMeshObjectIds(): number[] {
    return Array.from(this.meshes.values()).map((mesh) => mesh.id);
  }

  public addText(text: ITextObject): void {
    if (this.disposed || this.entities.has(text.guid)) {
      return;
    }

    this.addEntity(text);
    this.flushBoundsIfDirty();
    this.viewer.dispatchEvent(
      new CustomEvent(TEXT_CREATED_EVENT, {
        detail: {
          modelId: this.modelId,
          text,
        },
      })
    );
  }

  /**
   * Re-adds a previously removed text entity without dispatching
   * {@link TEXT_CREATED_EVENT}. Use this for undo/redo restore so the
   * text-creation coordinator does not fire a duplicate persist cycle.
   */
  public restoreText(text: ITextObject): void {
    if (this.disposed || this.entities.has(text.guid)) {
      return;
    }

    this.addEntity(text);
    this.flushBoundsIfDirty();
  }

  public removeText(guid: string): void {
    const entity = this.entities.get(guid);
    if (this.disposed || !entity) {
      return;
    }

    const snapshot = entity.toObject();
    this.removeEntity(guid, { emitSelectionChanged: true });
    this.flushBoundsIfDirty();
    this.viewer.dispatchEvent(
      new CustomEvent(TEXT_DELETED_EVENT, {
        detail: {
          modelId: this.modelId,
          text: snapshot,
        },
      })
    );
  }

  public attachPreviewMesh(mesh: TextMesh): void {
    this.textGroup.add(mesh);
  }

  public detachPreviewMesh(mesh: TextMesh): void {
    this.textGroup.remove(mesh);
    mesh.dispose();
  }

  public getEntitiesForSpace(modelSpaceId: string): TextEntity[] {
    return this.allEntities().filter(
      (entity) => entity.modelSpaceId === modelSpaceId
    );
  }

  public async removeSpace(modelSpaceId: string): Promise<void> {
    const previousSelection = [...this.selectedGuids];
    const removeGuids = this.getEntitiesForSpace(modelSpaceId).map(
      (entity) => entity.guid
    );
    for (const guid of removeGuids) {
      this.removeEntity(guid, { emitSelectionChanged: false });
    }
    const remainingSelectedGuids = previousSelection.filter(
      (guid) => !removeGuids.includes(guid)
    );
    if (
      !areTextGuidSelectionsEqual(remainingSelectedGuids, previousSelection)
    ) {
      this.setSelectedEntities(remainingSelectedGuids);
      this.emitSelectionChanged();
    }
    this.flushBoundsIfDirty();
    await Promise.resolve();
  }

  public getSelectedEntities(): TextEntity[] {
    return this.selectedGuids
      .map((guid) => this.entities.get(guid))
      .filter((entity): entity is TextEntity => entity !== undefined);
  }

  public setSelectedEntities(guids: string[]): void {
    this.selectedGuids = [...guids];
    for (const textMesh of this.meshes.values()) {
      const selected = this.selectedGuids.includes(textMesh.guid);
      textMesh.traverse((child) => {
        child.userData['selected'] = selected;
      });
    }
    this.syncInsertionPointHandles();
  }

  public getEntityByGuid(guid: string): TextEntity | undefined {
    return this.entities.get(guid);
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 18 > 15 allowed; refactor later
  public updateEntity(guid: string, patch: TextObjectPatch): void {
    const entity = this.entities.get(guid);
    if (!entity) {
      return;
    }

    if (patch.content !== undefined) {
      entity.content = patch.content;
    }
    if (patch.insertionPoint !== undefined) {
      entity.insertionPoint = { ...patch.insertionPoint };
    }
    if (patch.textHeight !== undefined) {
      entity.textHeight = patch.textHeight;
    }
    if (patch.rotation !== undefined) {
      entity.rotation = patch.rotation;
    }
    if (patch.horizontalAttachment !== undefined) {
      entity.horizontalAttachment = patch.horizontalAttachment;
    }
    if (patch.verticalAttachment !== undefined) {
      entity.verticalAttachment = patch.verticalAttachment;
    }
    if (patch.typefaceName !== undefined) {
      entity.typefaceName = patch.typefaceName;
    }
    if (patch.widthFactor !== undefined) {
      entity.widthFactor = patch.widthFactor;
    }
    if (patch.obliqueAngle !== undefined) {
      entity.obliqueAngle = patch.obliqueAngle;
    }
    if (patch.boxWidth !== undefined) {
      entity.boxWidth = patch.boxWidth;
    }
    if (patch.lineSpacingFactor !== undefined) {
      entity.lineSpacingFactor = patch.lineSpacingFactor;
    }
    if (patch.lineSpacingStyle !== undefined) {
      entity.lineSpacingStyle = patch.lineSpacingStyle;
    }
    if (patch.drawDirection !== undefined) {
      entity.drawDirection = patch.drawDirection;
    }
    if (patch.color !== undefined) {
      entity.color = patch.color;
    }
    if (patch.editable !== undefined) {
      entity.editable = patch.editable;
    }
    if (patch.modelSpaceId !== undefined) {
      entity.modelSpaceId = patch.modelSpaceId;
    }
    if (patch.metaData !== undefined) {
      entity.metaData = { ...entity.metaData, ...patch.metaData };
    }

    this.syncEntityMesh(guid, entity.toObject());
    this.markBoundsDirty();
    this.refreshSelectionHighlight(guid);
    this.syncInsertionPointHandles();
    this.flushBoundsIfDirty();
  }

  private updateInsertionPointHandlePosition(guid: string): void {
    const handle = this.insertionPointHandles.get(guid);
    const entity = this.entities.get(guid);
    if (!handle || !entity) {
      return;
    }
    handle.setPosition(
      new Vector3(
        entity.insertionPoint.x,
        entity.insertionPoint.y,
        entity.insertionPoint.z
      )
    );
  }

  private syncInsertionPointHandles(): void {
    const selectedEditableGuids = new Set(
      this.selectedGuids.filter((guid) => this.entities.get(guid)?.editable)
    );

    for (const [guid, handle] of [...this.insertionPointHandles.entries()]) {
      if (!selectedEditableGuids.has(guid)) {
        this.handleGroup.remove(handle.object3D);
        handle.dispose();
        this.insertionPointHandles.delete(guid);
      }
    }

    for (const guid of selectedEditableGuids) {
      if (this.insertionPointHandles.has(guid)) {
        this.updateInsertionPointHandlePosition(guid);
        continue;
      }

      const entity = this.entities.get(guid);
      const mesh = this.meshes.get(guid);
      if (!entity || !mesh) {
        continue;
      }

      const insertionPoint = entity.insertionPoint;
      const position = new Vector3(
        insertionPoint.x,
        insertionPoint.y,
        insertionPoint.z
      );
      const handle = new InsertionPointHandle(
        this.api,
        position,
        () => this.getDragConstraintSettings(),
        (nextPosition) => {
          mesh.setInsertionPointPreview({
            x: nextPosition.x,
            y: nextPosition.y,
            z: nextPosition.z,
          });
        },
        (nextPosition) => {
          this.emitInsertionPointChanged(guid, nextPosition);
          const currentEntity = this.entities.get(guid);
          const currentMesh = this.meshes.get(guid);
          if (currentEntity && currentMesh) {
            currentMesh.setInsertionPointPreview(currentEntity.insertionPoint);
            handle.setPosition(
              new Vector3(
                currentEntity.insertionPoint.x,
                currentEntity.insertionPoint.y,
                currentEntity.insertionPoint.z
              )
            );
          }
        }
      );
      this.insertionPointHandles.set(guid, handle);
      this.handleGroup.add(handle.object3D);
    }
  }

  private emitInsertionPointChanged(guid: string, position: Vector3): void {
    this.viewer.dispatchEvent(
      new CustomEvent(TEXT_INSERTION_POINT_CHANGED_EVENT, {
        detail: {
          modelId: this.modelId,
          guid,
          insertionPoint: {
            x: position.x,
            y: position.y,
            z: position.z,
          },
        },
      })
    );
  }

  private refreshSelectionHighlight(guid: string): void {
    if (!this.selectedGuids.includes(guid)) {
      return;
    }

    const selectedIds = this.viewer.selection.get('threejs');
    if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
      return;
    }

    const selectableModel = this.threejsModel as ThreejsModel & {
      _setSelection(ids: number[]): void;
    };
    selectableModel._setSelection([...selectedIds]);
  }

  public async upsertSpaceTexts(
    modelSpaceId: string,
    texts: ITextObject[],
    keepInSpace: (text: ITextObject) => boolean
  ): Promise<void> {
    const previousSelection = [...this.selectedGuids];
    const incomingByGuid = new Map(texts.map((text) => [text.guid, text]));

    for (const [guid, entity] of [...this.entities.entries()]) {
      if (entity.modelSpaceId !== modelSpaceId) {
        continue;
      }
      const candidate = incomingByGuid.get(guid) ?? entity.toObject();
      if (!keepInSpace(candidate)) {
        this.removeEntity(guid, { emitSelectionChanged: false });
      }
    }

    const toAdd: ITextObject[] = [];
    const toUpdate: ITextObject[] = [];

    for (const text of texts) {
      const tagged: ITextObject = { ...text, modelSpaceId };
      if (!keepInSpace(tagged)) {
        continue;
      }

      const existing = this.entities.get(text.guid);
      if (!existing) {
        toAdd.push(tagged);
        continue;
      }

      if (!textsEqual(existing.toObject(), tagged)) {
        toUpdate.push(tagged);
      }
    }

    await this.addEntitiesInChunks(toAdd);
    await this.updateEntitiesInChunks(toUpdate);

    if (toUpdate.length > 0) {
      this.markBoundsDirty();
      this.syncInsertionPointHandles();
    }

    this.flushBoundsIfDirty();
    this.emitSelectionChangedIfNeeded(previousSelection);
    await Promise.resolve();
  }

  public async upsertAndClean(
    texts: ITextObject[],
    keep: (text: ITextObject) => boolean
  ): Promise<void> {
    const previousSelection = [...this.selectedGuids];
    const incomingByGuid = new Map(texts.map((text) => [text.guid, text]));

    for (const [guid, entity] of [...this.entities.entries()]) {
      const candidate = incomingByGuid.get(guid) ?? entity.toObject();
      if (!keep(candidate)) {
        this.removeEntity(guid, { emitSelectionChanged: false });
      }
    }

    const toAdd: ITextObject[] = [];
    const toUpdate: ITextObject[] = [];

    for (const text of texts) {
      if (!keep(text)) {
        continue;
      }

      const existing = this.entities.get(text.guid);
      if (!existing) {
        toAdd.push(text);
        continue;
      }

      if (!textsEqual(existing.toObject(), text)) {
        toUpdate.push(text);
      }
    }

    await this.addEntitiesInChunks(toAdd);
    await this.updateEntitiesInChunks(toUpdate);

    if (toUpdate.length > 0) {
      this.markBoundsDirty();
      this.syncInsertionPointHandles();
    }

    this.flushBoundsIfDirty();
    this.emitSelectionChangedIfNeeded(previousSelection);
    await Promise.resolve();
  }

  private syncEntityMesh(guid: string, textObject: ITextObject): void {
    const existingMesh = this.meshes.get(guid);
    if (!existingMesh) {
      return;
    }

    existingMesh.update(textObject);
  }

  private async addEntitiesInChunks(texts: ITextObject[]): Promise<void> {
    for (let index = 0; index < texts.length; index += TEXT_UPSERT_BATCH_SIZE) {
      if (this.disposed) {
        return;
      }

      const batch = texts.slice(index, index + TEXT_UPSERT_BATCH_SIZE);
      for (const text of batch) {
        if (this.disposed) {
          return;
        }
        this.addEntity(text);
      }
      if (index + TEXT_UPSERT_BATCH_SIZE < texts.length) {
        await yieldToMainThread();
      }
    }
  }

  private markBoundsDirty(): void {
    this.boundsDirty = true;
  }

  private flushBoundsIfDirty(): void {
    if (!this.boundsDirty) {
      return;
    }
    this.boundsDirty = false;
    this.threejsModel.refreshBoundingBox();
    this.api.renderingManager.redraw();
  }

  private async updateEntitiesInChunks(texts: ITextObject[]): Promise<void> {
    for (let index = 0; index < texts.length; index += TEXT_UPSERT_BATCH_SIZE) {
      if (this.disposed) {
        return;
      }

      const batch = texts.slice(index, index + TEXT_UPSERT_BATCH_SIZE);
      for (const text of batch) {
        if (this.disposed) {
          return;
        }
        const updated = new TextEntity(text);
        this.entities.set(text.guid, updated);
        this.syncEntityMesh(text.guid, updated.toObject());
        this.refreshSelectionHighlight(text.guid);
      }
      if (index + TEXT_UPSERT_BATCH_SIZE < texts.length) {
        await yieldToMainThread();
      }
    }
  }

  private addEntity(text: ITextObject): void {
    const entity = new TextEntity(text);
    const mesh = new TextMesh(entity.toObject());
    this.entities.set(text.guid, entity);
    this.meshes.set(text.guid, mesh);
    this.textGroup.add(mesh);
    this.markBoundsDirty();
  }

  private removeEntity(
    guid: string,
    options: { readonly emitSelectionChanged?: boolean } = {}
  ): void {
    const handle = this.insertionPointHandles.get(guid);
    if (handle) {
      this.handleGroup.remove(handle.object3D);
      handle.dispose();
      this.insertionPointHandles.delete(guid);
    }

    const wasSelected = this.selectedGuids.includes(guid);
    this.selectedGuids = this.selectedGuids.filter((id) => id !== guid);

    const mesh = this.meshes.get(guid);
    if (mesh) {
      this.evictMeshFromViewerSelection(mesh);
      this.textGroup.remove(mesh);
      mesh.dispose();
      this.meshes.delete(guid);
    }
    this.entities.delete(guid);
    this.markBoundsDirty();

    if (options.emitSelectionChanged && wasSelected) {
      this.emitSelectionChanged();
    }
  }

  /**
   * Removes all Three.js object IDs belonging to {@link mesh} from the
   * viewer's `threejs` selection so that {@link GenericModel._setSelection}
   * never references a detached / disposed object.
   *
   * `selectedGuids` must be updated *before* calling this method so the
   * re-entrant `handleViewerSelection` listener sees an unchanged set and
   * bails out early.
   */
  private evictMeshFromViewerSelection(mesh: TextMesh): void {
    const selectedIds = this.viewer.selection.get('threejs');
    if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
      return;
    }

    const meshIds = new Set<number>();
    mesh.traverse((child) => {
      meshIds.add(child.id);
    });

    if (!selectedIds.some((id) => meshIds.has(id))) {
      return;
    }

    const remaining = selectedIds.filter((id) => !meshIds.has(id));
    if (remaining.length > 0) {
      this.viewer.selection.set('threejs', remaining);
    } else {
      this.viewer.selection.delete('threejs');
    }
  }

  private emitSelectionChangedIfNeeded(previousSelection: string[]): void {
    if (!areTextGuidSelectionsEqual(previousSelection, this.selectedGuids)) {
      this.emitSelectionChanged();
    }
  }

  private emitSelectionChanged(): void {
    this.viewer.dispatchEvent(
      new CustomEvent(TEXT_SELECTION_CHANGED_EVENT, {
        detail: {
          modelId: this.modelId,
          guids: [...this.selectedGuids],
        },
      })
    );
  }

  private getTextGuidsFromViewerThreeJsSelection(): string[] {
    const selectedThreeJsModelIds = this.viewer.selection.get('threejs');
    if (!Array.isArray(selectedThreeJsModelIds)) {
      return [];
    }

    return collectTextGuidsFromObjectIds(
      this.threejsModel.root,
      selectedThreeJsModelIds
    );
  }
}
